/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { parseActionCodeURL } from 'firebase/auth';
import * as React from 'react';

import { getApplicationEndpoint } from '@gitbook/env';
import { parseUrl } from '@gitbook/util-url';

import { useHistoryLocation } from '../../navigation';

const FIREBASE_ACTION_OPERATION_TO_MODE = {
    EMAIL_SIGNIN: 'signIn',
    PASSWORD_RESET: 'resetPassword',
    RECOVER_EMAIL: 'recoverEmail',
    VERIFY_EMAIL: 'verifyEmail',
    VERIFY_AND_CHANGE_EMAIL: 'verifyAndChangeEmail',
} as const;

/**
 * https://firebase.google.com/docs/reference/js/auth.actioncodeurl#properties
 */
const FIREBASE_ACTION_QUERY_PARAMS = [
    'apiKey',
    'continueUrl',
    'lang',
    'mode',
    'oobCode',
    'tenantId',
] as const;

export interface FirebaseActionURLParams {
    mode: (typeof FIREBASE_ACTION_OPERATION_TO_MODE)[keyof typeof FIREBASE_ACTION_OPERATION_TO_MODE];
    oobCode: string;
    continueUrl?: string;
}

/**
 * Get the firebase action URL params.
 * https://firebase.google.com/docs/auth/custom-email-handler
 */
export function useFirebaseActionURLParams(): FirebaseActionURLParams | null {
    const location = useHistoryLocation();

    return React.useMemo(() => {
        return extractFirebaseActionURLParams(window.location.href);
    }, [location.search]);
}

/** Extract firebase action from URL. */
function extractFirebaseActionURLParams(url: string): FirebaseActionURLParams | null {
    const action = parseActionCodeURL(url);

    if (!action) {
        return null;
    }

    const mode =
        FIREBASE_ACTION_OPERATION_TO_MODE[
            action.operation as keyof typeof FIREBASE_ACTION_OPERATION_TO_MODE
        ];

    if (!mode) {
        return null;
    }

    return {
        mode,
        oobCode: action.code,
        continueUrl: getSafeContinueURL(action.continueUrl),
    };
}

/**
 * Remove Firebase action parameters from a search string while keeping app parameters intact.
 */
export function stripFirebaseActionURLParams(search: string): string {
    const searchParams = new URLSearchParams(search);
    for (const key of FIREBASE_ACTION_QUERY_PARAMS) {
        searchParams.delete(key);
    }

    const stripped = searchParams.toString();
    return stripped ? `?${stripped}` : '';
}

/** Remove the custom auth token while keeping application parameters intact. */
export function stripCustomAuthTokenURLParams(search: string): string {
    const searchParams = new URLSearchParams(search);
    searchParams.delete('auth_token');

    const stripped = searchParams.toString();
    return stripped ? `?${stripped}` : '';
}

/**
 * Validate and sanitize the continue URL to ensure it points to the GitBook app.
 * This prevents open redirect vulnerabilities by only allowing URLs with the same origin.
 */
function getSafeContinueURL(continueUrl: string | null): string | undefined {
    if (typeof continueUrl !== 'string') {
        return undefined;
    }

    try {
        // IMPORTANT: Sometimes we need to decode the continueUrl before parsing it, as it can be double-encoded (like the redirect parameter)
        // For ex: continueUrl=https%3A%2F%2Fapp.gitbook.com%2Fo%2Fd8f63b60-89ae-11e7-8574-5927d48c4877%2Fsites%2Fsite_p4Xo4%2Fpreview%2Fauth%2F%3Fredirect%3Dhttps%253A%252F%252Fsites.gitbook.com%252Fpreview%252Fsite_p4Xo4&lang=en
        // If we did not decode it, parseUrl would fail. Because sometimes the continueUrl itself can contain a redirect URL as a query parameter which is
        // already URL-encoded, so it becomes double-encoded when included as a query parameter in the continueUrl. Thus, the necessary decoding step before parsing.
        const parsedURL = parseUrl(continueUrl) ?? parseUrl(decodeURIComponent(continueUrl));
        if (!parsedURL) {
            return undefined;
        }
        const appOrigin = new URL(getApplicationEndpoint()).origin;

        if (parsedURL.origin === appOrigin) {
            return parsedURL.toString();
        }

        if (process.env.NODE_ENV === 'development') {
            if (parsedURL.hostname === 'localhost' || parsedURL.hostname === '127.0.0.1') {
                return parsedURL.toString();
            }
        }

        return undefined;
    } catch {
        return undefined;
    }
}
